import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface PractitionerHighlight {
  id: string;
  type: 'total_trainings' | 'new_connections' | 'most_frequent_partner' | 'average_rating' | 'total_hours' | 'most_applied_technique' | 'schedule_status' | 'recognitions' | 'unique_partners' | 'interactions' | 'achievement';
  title: string;
  value: string | number;
  description?: string;
  icon?: string;
  // Additional fields for specific card types
  partnerInfo?: {
    id: string;
    name: string;
    profilePictureUrl?: string;
    beltRank?: string;
    trainingCount: number;
  };
  techniqueInfo?: {
    name: string;
    count: number;
  };
  achievementInfo?: {
    milestone: number;
    title: string;
    description: string;
    emojis: string[];
    numberText: string;
  };
  scheduleStatusData?: {
    completed: number;
    missed: number;
    total: number;
    completedPercentage: number;
    missedPercentage: number;
  };
}

export interface PractitionerHighlightsData {
  totalTrainings: {
    group: number;
    private: number;
    total: number;
  };
  newConnections: number;
  mostFrequentPartner: {
    id: string;
    name: string;
    profilePictureUrl?: string;
    beltRank?: string;
    trainingCount: number;
  } | null;
  averageRating: number;
  totalHours: number;
  mostAppliedTechnique: {
    name: string;
    count: number;
  } | null;
  scheduleStatus: {
    completed: number;
    missed: number;
    completedPercentage: number;
    missedPercentage: number;
  };
  recognitions: number;
  uniquePartners: number;
  interactions: number;
  achievement: {
    milestone: number;
    title: string;
    description: string;
    emoji: string;
  } | null;
}

export const usePractitionerHighlights = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: highlightsData, isLoading, error, refetch } = useQuery({
    queryKey: ['practitioner-highlights', user?.id],
    queryFn: async (): Promise<PractitionerHighlightsData> => {
      if (!user?.id) throw new Error('User not authenticated');

      // Get current month boundaries
      const now = new Date();
      const currentMonth = now.getMonth() + 1;
      const currentYear = now.getFullYear();
      const startOfMonth = new Date(currentYear, currentMonth - 1, 1);
      const endOfMonth = new Date(currentYear, currentMonth, 0, 23, 59, 59);

      // 1. Total Trainings Practiced (Group x Private)
      const { data: trainingsData, error: trainingsError } = await supabase
        .from('trainings')
        .select('id, type, status, start_time, end_time')
        .eq('user_id', user.id)
        .eq('status', 'Completed')
        .gte('start_time', startOfMonth.toISOString())
        .lte('start_time', endOfMonth.toISOString());

      if (trainingsError) throw trainingsError;

      const totalTrainings = {
        group: trainingsData?.filter(t => t.type === 'Group Class').length || 0,
        private: trainingsData?.filter(t => t.type === 'Private Class').length || 0,
        total: trainingsData?.length || 0
      };

      // 2. New Connections
      const { data: connectionsData, error: connectionsError } = await supabase
        .from('connections')
        .select('id')
        .or(`requester.eq.${user.id},recipient.eq.${user.id}`)
        .eq('status', 'connected')
        .gte('created_at', startOfMonth.toISOString());

      if (connectionsError) throw connectionsError;
      const newConnections = connectionsData?.length || 0;

      // 3. Partner You Most Roll With
      let partnerData = null;
      let partnerError = null;
      
      if (trainingsData && trainingsData.length > 0) {
        const trainingIds = trainingsData.map(t => t.id);
        
        const { data, error } = await supabase
          .from('training_partners')
          .select(`
            partner_user_id,
            profiles!inner(
              id,
              full_name,
              profile_picture_url,
              belt_rank
            )
          `)
          .in('training_id', trainingIds);
        
        partnerData = data;
        partnerError = error;
      }

      if (partnerError) throw partnerError;

      let mostFrequentPartner = null;
      if (partnerData && partnerData.length > 0) {
        // Count occurrences of each partner
        const partnerCounts = partnerData.reduce((acc, partner) => {
          const partnerId = partner.partner_user_id;
          if (!acc[partnerId]) {
            acc[partnerId] = {
              ...partner.profiles,
              count: 0
            };
          }
          acc[partnerId].count++;
          return acc;
        }, {} as Record<string, any>);

        // Find the most frequent partner
        const mostFrequent = Object.values(partnerCounts).reduce((max, partner) => 
          partner.count > max.count ? partner : max
        );

        mostFrequentPartner = {
          id: mostFrequent.id,
          name: mostFrequent.full_name,
          profilePictureUrl: mostFrequent.profile_picture_url,
          beltRank: mostFrequent.belt_rank,
          trainingCount: mostFrequent.count
        };
      }

      // 4. Training Average Rating
      let ratingData = null;
      let ratingError = null;
      
      if (trainingsData && trainingsData.length > 0) {
        const trainingIds = trainingsData.map(t => t.id);
        const { data, error } = await supabase
          .from('training_notes')
          .select('overall_rating')
          .in('training_id', trainingIds)
          .not('overall_rating', 'is', null);
        
        ratingData = data;
        ratingError = error;
      }

      if (ratingError) throw ratingError;
      const averageRating = ratingData && ratingData.length > 0 
        ? ratingData.reduce((sum, note) => sum + note.overall_rating, 0) / ratingData.length 
        : 0;

      // 5. Total Hours in the Mat
      const { data: hoursData, error: hoursError } = await supabase
        .from('trainings')
        .select('start_time, end_time')
        .eq('user_id', user.id)
        .eq('status', 'Completed')
        .gte('start_time', startOfMonth.toISOString())
        .lte('start_time', endOfMonth.toISOString());

      if (hoursError) throw hoursError;
      const totalHours = hoursData?.reduce((total, training) => {
        const start = new Date(training.start_time);
        const end = new Date(training.end_time);
        const duration = (end.getTime() - start.getTime()) / (1000 * 60 * 60); // Convert to hours
        return total + duration;
      }, 0) || 0;

      // 6. Most Applied Technique
      let techniqueData = null;
      let techniqueError = null;
      
      if (trainingsData && trainingsData.length > 0) {
        const trainingIds = trainingsData.map(t => t.id);
        const { data, error } = await supabase
          .from('training_techniques')
          .select(`
            technique_id,
            techniques!inner(name)
          `)
          .in('training_id', trainingIds);
        
        techniqueData = data;
        techniqueError = error;
      }

      if (techniqueError) throw techniqueError;

      let mostAppliedTechnique = null;
      if (techniqueData && techniqueData.length > 0) {
        const techniqueCounts = techniqueData.reduce((acc, item) => {
          const techniqueName = item.techniques.name;
          acc[techniqueName] = (acc[techniqueName] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        const mostApplied = Object.entries(techniqueCounts).reduce((max, [name, count]) => 
          count > max.count ? { name, count } : max
        , { name: '', count: 0 });

        if (mostApplied.count > 0) {
          mostAppliedTechnique = mostApplied;
        }
      }

      // 7. Your Schedule Status
      const { data: scheduleData, error: scheduleError } = await supabase
        .from('trainings')
        .select('status')
        .eq('user_id', user.id)
        .gte('start_time', startOfMonth.toISOString())
        .lte('start_time', endOfMonth.toISOString());

      if (scheduleError) throw scheduleError;

      const completed = scheduleData?.filter(t => t.status === 'Completed').length || 0;
      const missed = scheduleData?.filter(t => t.status === 'Missed').length || 0;
      const total = completed + missed; // Only count finalized trainings
      const scheduleStatus = {
        completed,
        missed,
        completedPercentage: total > 0 ? Math.round((completed / total) * 100) : 0,
        missedPercentage: total > 0 ? Math.round((missed / total) * 100) : 0
      };

      // 8. Recognitions (from user_recognitions table)
      const { data: recognitionsData, error: recognitionsError } = await supabase
        .from('user_recognitions')
        .select('recognition_count')
        .eq('user_id', user.id)
        .gte('created_at', startOfMonth.toISOString());

      if (recognitionsError) throw recognitionsError;
      const recognitions = recognitionsData?.reduce((sum, rec) => sum + rec.recognition_count, 0) || 0;

      // 9. Partners You've Rolled With
      const uniquePartners = new Set(partnerData?.map(p => p.partner_user_id) || []).size;

      // 10. Interactions
      const { data: postsData } = await supabase
        .from('posts')
        .select('id')
        .eq('user_id', user.id)
        .gte('created_at', startOfMonth.toISOString());

      const { data: likesData } = await supabase
        .from('post_likes')
        .select('id')
        .eq('user_id', user.id)
        .gte('created_at', startOfMonth.toISOString());

      const { data: commentsData } = await supabase
        .from('post_comments')
        .select('id')
        .eq('user_id', user.id)
        .gte('created_at', startOfMonth.toISOString());

      const interactions = (postsData?.length || 0) + (likesData?.length || 0) + (commentsData?.length || 0);

      // 11. Completed Trainings Achievement
      const { data: allTrainingsData } = await supabase
        .from('trainings')
        .select('id')
        .eq('user_id', user.id)
        .eq('status', 'Completed');

      const totalCompleted = allTrainingsData?.length || 0;
      console.log('Total completed trainings:', totalCompleted);
      let achievement = null;

      if (totalCompleted >= 1000) {
        achievement = {
          milestone: 1000,
          title: "The Thousand Hour Warrior",
          description: "Legacy of Discipline",
          emojis: ["⚔️", "📜"],
          numberText: "1000th"
        };
      } else if (totalCompleted >= 500) {
        achievement = {
          milestone: 500,
          title: "Half a Thousand Rolls",
          description: "Road to Mastery",
          emojis: ["🏅", "🗺️"],
          numberText: "500th"
        };
      } else if (totalCompleted >= 200) {
        achievement = {
          milestone: 200,
          title: "The Double Century",
          description: "A Mat Veteran",
          emojis: ["⭐", "👑"],
          numberText: "200th"
        };
      } else if (totalCompleted >= 100) {
        achievement = {
          milestone: 100,
          title: "The Century Mark",
          description: "100 Rolls Strong",
          emojis: ["💯", "🔥"],
          numberText: "100th"
        };
      } else if (totalCompleted >= 50) {
        achievement = {
          milestone: 50,
          title: "Half a Century of Rolls",
          description: "Fifty Times Stronger",
          emojis: ["🗓️", "✨"],
          numberText: "50th"
        };
      } else if (totalCompleted >= 25) {
        achievement = {
          milestone: 25,
          title: "Quarter Century",
          description: "Twenty-Five Strong",
          emojis: ["⭐", "💪"],
          numberText: "25th"
        };
      } else if (totalCompleted >= 10) {
        achievement = {
          milestone: 10,
          title: "Ten and Counting",
          description: "Double-Digit Dedication",
          emojis: ["🔟", "💪"],
          numberText: "10th"
        };
      } else if (totalCompleted >= 1) {
        achievement = {
          milestone: 1,
          title: "The First Step",
          description: "On the sOSSiety Mat!",
          emojis: ["👣", "👣", "🥋"],
          numberText: "1st"
        };
      }

      return {
        totalTrainings,
        newConnections,
        mostFrequentPartner,
        averageRating,
        totalHours,
        mostAppliedTechnique,
        scheduleStatus,
        recognitions,
        uniquePartners,
        interactions,
        achievement
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  return {
    highlights: highlightsData,
    isLoading,
    error,
    refetch
  };
};
